import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import lodash from "lodash";
import useErrorBoundary from "use-error-boundary";
import momentTimeZone from "moment-timezone";
import { Tab, Box, IconButton, CircularProgress } from "@mui/material";
import { TabContext, TabList } from "@mui/lab";
import RefreshIcon from "@mui/icons-material/Refresh"; // Added Refresh icon
import { toast } from "react-toastify";

import useStyles from "../views/components/Auction/AuctionDetail/auctionDetail.style";
import muiComponentStyles from "./LotCard.styles";
import AuctionLotDialog from "./BidPlacementDialog";
import AuctionLotBidsDialog from "./AuctionLotBidsDialog";
import createListLots from "../views/components/Auction/AuctionDetail/createListLots";
import ToggleViewButtons from "../views/components/Auction/AuctionDetail/ToggleViewButtons";
import RenderTabPanel from "../views/components/Auction/AuctionDetail/RenderTabPanel";
import { getToken, getUserData } from "../common";
import { getEnglishBidError } from "./englishBidValidation";
import setAutobidAPI from "../api/setAutobidAPI";
import { now, syncServerTime } from "../common/dateTime";
import { ChevronRight } from "@mui/icons-material";

const AuctionLotsContainer = ({
  finalizeLot,
  auctionId,
  fetchSingleLotBidStatus,
  ...props
}) => {
  const classes = useStyles();
  const styles = muiComponentStyles();
  const { ErrorBoundary, didCatch, error } = useErrorBoundary();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeDialog, toggleDialog] = useState(false);
  const [activeTab, switchTab] = useState("all-lots");
  const [view, setView] = useState("card"); // list or card view
  const [userAmount, setUserAmount] = useState({});
  const [proxyLotAmounts, setProxyLotAmounts] = useState({});
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [loadingProxyBidFor, setLoadingProxyBidFor] = useState(null);
  const [dialogProps, updateDialogProps] = useState({
    eventDetails: {},
    lotDetails: {},
  });
  const [isRefreshing, setIsRefreshing] = useState(false); // Add refreshing state
  const [tabPanelDetails, setTabPanelDetails] = useState([
    {
      value: "all-lots",
      lots: props.allLots,
      columns: [],
      listLots: [],
    },
    {
      value: "my-lots",
      lots: props.userLots,
      columns: [],
      listLots: [],
    },
    {
      value: "proxy-bid",
      lots: props.userLots,
      columns: [],
      listLots: [],
    },
    {
      value: "proxy-bid",
      lots: props.userLots,
      columns: [],
      listLots: [],
    },
  ]);

  // Update dialog props when lots update (for real-time bid/timer updates)
  useEffect(() => {
    if (activeDialog && dialogProps.lotDetails?.eventItemId) {
      const updatedLot = props.allLots.find(
        (lot) => lot.eventItemId === dialogProps.lotDetails.eventItemId
      );
      if (updatedLot) {
        updateDialogProps((prev) => ({
          ...prev,
          lotDetails: updatedLot,
          // for Japanese/Dutch, update payload amount if needed, though handleAuctionDialog usually sets it.
          // We might want to keep userAmount separate if user is typing, but for Jap auction, amount is fixed by phase usually?
          // For now just update lot details to refresh timer/price display.
          userAmount:
            props.eventsDetails.eventFormat === "JAPANESE_AUCTION" ||
              props.eventsDetails.eventFormat === "DUTCH_AUCTION"
              ? updatedLot.currentPrice
              : prev.userAmount,
        }));
      }
    }
  }, [props.allLots, activeDialog, dialogProps.lotDetails?.eventItemId]);

  useEffect(() => {
    if (getToken()) {
      setIsAuthenticated(true);
    }
  }, []);

  const isLotLive = (lot) => {
    if (!lot) return false;
    const timeZone =
      localStorage.getItem("currentTimeZone") || momentTimeZone.tz.guess();
    return (
      lot.status === "live" ||
      now().tz(timeZone).isSameOrAfter(moment(lot.startDateAndTime).tz(timeZone))
    );
  };

  useEffect(() => {
    const handleStatusChange = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener("online", handleStatusChange);
    window.addEventListener("offline", handleStatusChange);

    return () => {
      window.removeEventListener("online", handleStatusChange);
      window.removeEventListener("offline", handleStatusChange);
    };
  }, [isOnline]);

  useEffect(() => {
    updateTabPanelDetails(0, props.allLots);
  }, [props.allLots, isOnline]);

  useEffect(() => {
    updateTabPanelDetails(1, props.userLots);

    // live lots for proxy bid
    const liveLots = props.userLots?.filter((lot) => isLotLive(lot)) || [];
    updateProxyLotAmounts(liveLots);
    updateTabPanelDetails(2, liveLots);
  }, [props.userLots, isOnline]);

  useEffect(() => {
    if (props.allLots) {
      // update user lot amounts
      const userLotAmounts = updateUserLotAmounts(props.allLots);
      panelDetailsReRender(0, props.allLots, userLotAmounts);
      panelDetailsReRender(1, props.userLots, userLotAmounts);
    }
  }, [props.allLots]);

  const updateUserLotAmounts = (lots) => {
    const updatedLotAmounts = lodash.cloneDeep(userAmount || {});
    lots.forEach(
      (lot) =>
      (updatedLotAmounts[`${lot.lotNumber}`] = Number(
        lot.nextBidAmount >= 0 ? lot.nextBidAmount : 0
      ))
    );
    setUserAmount(updatedLotAmounts || {});
    return updatedLotAmounts;
  };

  const setProxyBid = async (eventDetails, proxyBidAmount) => {
    const eventType = eventDetails.event.eventType;
    const nextBidAmount = eventDetails.nextBidAmount;

    if (eventType === "REVERSE_AUCTION" && proxyBidAmount >= nextBidAmount) {
      return toast.info(`Proxy bid amount must be less than ${nextBidAmount}`);
    }

    if (eventType === "FORWARD_AUCTION" && proxyBidAmount <= nextBidAmount) {
      return toast.info(
        `Proxy bid amount must be greater than ${nextBidAmount}`
      );
    }

    const eventId = eventDetails.eventId;
    const eventItemId = eventDetails.eventItemId;
    setLoadingProxyBidFor(eventItemId);

    try {
      const response = await setAutobidAPI(
        { amount: proxyBidAmount },
        eventId,
        eventItemId
      );
      setLoadingProxyBidFor(null);

      // update current proxy bid amount for live lots
      const liveLots =
        props.userLots
          ?.filter((lot) => isLotLive(lot))
          ?.map((lot) => {
            if (lot.eventItemId == eventItemId)
              lot.proxyBidAmount = proxyBidAmount;
            return lot;
          }) || [];

      updateTabPanelDetails(2, liveLots);

      return toast.success(
        `Proxy bid on lot ${eventDetails.lotNumber} set successfully!`
      );
    } catch (error) {
      setLoadingProxyBidFor(null);
      return toast.error(error.response.data.error || "Something went wrong!");
    }
  };

  const updateProxyLotAmounts = (lots) => {
    const updatedProxyLotAmounts = lodash.cloneDeep(proxyLotAmounts || {});
    lots.forEach(
      (lot) =>
      (updatedProxyLotAmounts[`${lot.lotNumber}`] = Number(
        lot.proxyBidAmount || (lot.nextBidAmount >= 0 ? lot.nextBidAmount : 0)
      ))
    );
    setProxyLotAmounts(updatedProxyLotAmounts || {});
    return updatedProxyLotAmounts;
  };

  const panelDetailsReRender = (index, lots, amounts) => {
    const timeZone =
      localStorage.getItem("currentTimeZone") || momentTimeZone.tz.guess();
    const currentTime = now().tz(timeZone).format();

    const isPreviewPeriodAllowed = props.eventsDetails?.previewPeriod !== "NO";

    lots =
      lots
        ?.map((lot) => {
          const startDateAndTime = moment(lot.startDateAndTime)
            .tz(timeZone)
            .format(); // lot start date

          if (isPreviewPeriodAllowed && currentTime < startDateAndTime) {
            lot.nextBidAmount = 0;
            lot.myBidStatus.myBidRank = null;
          }

          return lot;
        })
        .filter((lot) => lot.concluded !== "YES") || [];

    const { columns, data } = createListLots({
      lots,
      styles,
      userAmount: amounts,
      eventDetails: props.eventsDetails,
      handleUserAmountChange,
      storeHandler,
      isForwardAuction,
      isOnline,
      lotBidEnded,
      panel: tabPanelDetails[index].value,
      handleProxyAmountChange,
      setProxyBid,
      refreshPanelDetails,
    });

    setTabPanelDetails((currentDetails) => {
      const updatedDetails = currentDetails?.map((details) =>
        lodash.cloneDeep(details)
      );

      updatedDetails[index] = {
        ...updatedDetails[index],
        columns,
        lots,
        listLots: data,
      };

      return updatedDetails;
    });
  };

  const updateTabPanelDetails = (index, lots) => {
    let userLotAmounts = lodash.cloneDeep(userAmount);
    if (lots.length && !Object.keys(userLotAmounts).length && index == 0) {
      userLotAmounts = updateUserLotAmounts(lots);
    }

    panelDetailsReRender(
      index,
      lots,
      index === 2 ? proxyLotAmounts : userLotAmounts
    );
  };

  const handleChange = (event, tabId) => {
    switchTab(tabId);
  };

  const handleAuctionDialog = useCallback(
    (props) => {
      toggleDialog((prevActiveDialog) => !prevActiveDialog);
      updateDialogProps(() => props);
    },
    [props.allLots, props.userLots, isOnline]
  );

  const [activeLotBidsDialog, toggleActiveLotBidsDialog] = useState(false);
  const [selectedLotBids, setSelectedLotBids] = useState(null);

  const handleAuctionLotBidsDialog = useCallback(
    (lotDetails) => {
      if (lotDetails && lotDetails.eventItemId) {
        setSelectedLotBids(lotDetails);
      }
      toggleActiveLotBidsDialog((prevActiveDialog) => !prevActiveDialog);
    },
    [props.allLots, props.userLots, isOnline]
  );

  const handleViewChange = (event, newView) => {
    setView(newView);
  };

  const handleUserAmountChange = (lotNumber, amount, amounts = {}) => {
    const current = lodash.cloneDeep(amounts || {});
    current[lotNumber] = Number(amount);
    setUserAmount(current || {});

    panelDetailsReRender(0, props.allLots, current);
    panelDetailsReRender(1, props.userLots, current);
  };

  const handleProxyAmountChange = (lotNumber, amount, amounts = {}) => {
    const current = lodash.cloneDeep(amounts || {});
    current[lotNumber] = Number(amount);
    setProxyLotAmounts(current || {});

    const liveLots = props.userLots?.filter((lot) => isLotLive(lot)) || [];

    panelDetailsReRender(2, liveLots, current);
  };

  const lotBidEnded = async (data) => {
    if (data.status !== "live") {
      props.refetchLotDetails();
      return;
    }
    finalizeLot(data.eventItemId);
  };

  const refreshPanelDetails = () => {
    // Trigger backend refetch to get latest status (e.g. Upcoming -> Live)
    if (props.refetchLotDetails) {
      props.refetchLotDetails();
    }
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      // the Refresh button also re-checks the server time
      await Promise.all([props.refetchLotDetails(), syncServerTime()]);
      toast.success("Lots refreshed successfully");
    } catch (error) {
      toast.error("Failed to refresh lots");
    } finally {
      setIsRefreshing(false);
    }
  };

  const storeHandler = (lotDetails, bidAmount) => {
    const eventDetails = props.eventsDetails;
    const eventFormat = eventDetails.eventFormat;

    const isEnglishAuction = eventFormat === "ENGLISH_AUCTION";
    const isJapaneseAuction = eventFormat === "JAPANESE_AUCTION";
    const isDutchAuction = eventFormat === "DUTCH_AUCTION";

    // const lotNumber = lotDetails.lotNumber;
    // const bidAmount = userAmount[`${lotNumber}`];
    // let { lotNumber, startDateAndTime } = lotDetails;
    // const { publishDateTime, bidStartDateTime } = eventDetails;
    const timeZone =
      localStorage.getItem("currentTimeZone") || momentTimeZone.tz.guess();

    const currentTime = now().tz(timeZone).format();
    const publishDateTime = moment(eventDetails.publishDateTime)
      .tz(timeZone)
      .format(); // event publish date
    const bidStartDateTime = moment(eventDetails.bidStartDateTime)
      .tz(timeZone)
      .format(); // event start date
    const startDateAndTime = moment(lotDetails.startDateAndTime)
      .tz(timeZone)
      .format(); // lot start date
    const endDateAndTime = moment(lotDetails.endDateAndTime)
      .tz(timeZone)
      .format(); // lot end date

    if (startDateAndTime > currentTime) {
      return toast.error(`Bidding hasn't started yet.`);
    }

    if (isJapaneseAuction || isDutchAuction) {
      return handleAuctionDialog({
        eventDetails,
        lotDetails,
        userAmount: lotDetails.currentPrice,
      });
    }

    // =========== rest conditions for english auctions only
    if (!bidAmount) {
      return toast.error(`Please enter the bidding amount.`);
    }
    if (bidAmount <= 0) {
      return toast.error(`Bidding amount must be greater than zero. `);
    }

    if (currentTime >= publishDateTime && currentTime <= bidStartDateTime) {
      return handleAuctionDialog({
        eventDetails,
        lotDetails,
        userAmount: bidAmount,
      });
    }

    const bidError = getEnglishBidError({
      eventDetails,
      lotDetails,
      amount: bidAmount,
    });
    if (bidError) {
      return toast.error(bidError);
    }

    return handleAuctionDialog({
      eventDetails,
      lotDetails,
      userAmount: bidAmount,
    });
  };

  const isForwardAuction = props.eventsDetails?.eventType === "FORWARD_AUCTION";

  const userDetails = getUserData() ?? {};

  return (
    <div className="container">
      {/* Dialogs */}
      <AuctionLotBidsDialog
        activeDialog={activeLotBidsDialog}
        handleAuctionDialog={handleAuctionLotBidsDialog}
        lotDetails={selectedLotBids}
      ></AuctionLotBidsDialog>
      <AuctionLotDialog
        dialogProps={dialogProps}
        activeDialog={activeDialog}
        handleAuctionDialog={handleAuctionDialog}
        fetchSingleLotBidStatus={fetchSingleLotBidStatus}
      ></AuctionLotDialog>

      <div className={classes.auctionTab}>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            width: "100%",
            gap: "16px", // Adds consistent spacing between items
          }}
        >
          <button
            style={{
              display: "flex",
              alignItems: "center",
              color: userDetails?.role !== "BUYER" ? "grey" : "blue",
              background: "none",
              border: "none",
              padding: "8px",
              cursor: userDetails?.role !== "BUYER" ? "default" : "pointer",
              fontSize: "16px",
            }}
            disabled={userDetails?.role !== "BUYER"}
            onClick={() => {
              window.location.href = `/#/auction/${auctionId}/post-bid`;
            }}
          >
            Post bid lots <ChevronRight fontSize="small" />
          </button>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              borderLeft: "1px solid #e0e0e0",
              paddingLeft: "16px",
            }}
          >
            <IconButton
              onClick={handleRefresh}
              disabled={isRefreshing}
              sx={{
                color: isRefreshing ? "grey" : "primary.main",
                padding: "8px",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
              size="small"
              aria-label="refresh lot details"
            >
              {isRefreshing ? (
                <CircularProgress size={20} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
              <span
                style={{
                  marginLeft: "8px",
                  fontSize: "16px",
                }}
              >
                Refresh
              </span>
            </IconButton>
          </div>
        </div>
        <TabContext value={activeTab}>
          <Box
            sx={{ borderBottom: 1, borderColor: "divider" }}
            style={{ display: "flex" }}
          >
            <TabList
              value={activeTab}
              onChange={handleChange}
              style={{ minWidth: "80%" }}
            >
              <Tab
                sx={{ fontSize: "19px", fontWeight: "bold" }}
                label={`All Lots (${lodash.size(props.allLots)})`}
                value="all-lots"
              />
              <Tab
                sx={{ fontSize: "19px", fontWeight: "bold" }}
                label={`My Lots (${lodash.size(props.userLots)})`}
                value="my-lots"
              />
              {isAuthenticated && (
                <Tab
                  sx={{ fontSize: "19px", fontWeight: "bold" }}
                  label={`Proxy Bid`}
                  value="proxy-bid"
                />
              )}
            </TabList>
            {activeTab != "proxy-bid" && (
              <ToggleViewButtons
                view={view}
                handleViewChange={handleViewChange}
              />
            )}
          </Box>

          {tabPanelDetails
            .filter((tabPanelDetails) =>
              isAuthenticated ? true : tabPanelDetails.value != "proxy-bid"
            )
            .map((tabPanelDetail, index) => (
              <RenderTabPanel
                {...tabPanelDetail}
                view={view}
                key={index}
                handleAuctionLotBidsDialog={handleAuctionLotBidsDialog}
                eventsDetails={props.eventsDetails}
                handleAuctionDialog={handleAuctionDialog}
                didCatch={didCatch}
                error={error}
                errorBoundary={ErrorBoundary}
                refreshPanelDetails={refreshPanelDetails}
                lotBidEnded={lotBidEnded}
              />
            ))}
        </TabContext>
      </div>
    </div>
  );
};

export default AuctionLotsContainer;
